import * as React from 'react'
import { Localized, withLocalization } from '@fluent/react'
import Icon from '../../components/ui/Icon'

import './index.css'

interface EditableTextProps {
  text: string
  placeholder: string
  minLength?: number,
  maxLength?: number,
  onAccept: (text: string) => void
  getString: (key: string) => string
}

type EditableTextError = 'editable-text-error-min-length' | 'editable-text-error-max-length'

interface EditableTextState {
  text: string,
  errors: EditableTextError[],
}

class EditableText extends React.Component<EditableTextProps> {
  state: EditableTextState = {
    text: this.props.text,
    errors: [],
  }

  componentDidUpdate(prevProps: EditableTextProps) {
    const prevText = prevProps.text
    const text = this.props.text
    if (prevText !== text) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ text: this.props.text })
    }
  }

  textRef: React.RefObject<HTMLSpanElement> = React.createRef()

  public render() {
    const { getString, placeholder } = this.props
    const placeholderText = this.state.text.trim() == '' ? getString(placeholder) : ''

    return (
      <form
        className="editable-text"
        onSubmit={this.onSubmit}
      >
        <div className="editable-text__input">
          <span
            className="editable-text__content"
            contentEditable
            onInput={this.handleNameChange}
            onKeyDown={this.onKeyDown}
            dangerouslySetInnerHTML={{ __html: this.props.text }}
            ref={this.textRef}
            data-testid="input"
            data-placeholder={placeholderText}
          />
          {
            this.state.text !== this.props.text ?
              <div className="editable-text__controls">
                <span
                  id="editable-text-accept"
                  onClick={this.onSubmit}
                  className={this.state.errors.length ? 'disabled' : ''}
                  data-testid="accept"
                >
                  <Icon size="medium" name="check" />
                </span>
                <span id="editable-text-cancel" onClick={this.cancelEdit} data-testid="cancel">
                  <Icon size="medium" name="close" />
                </span>
              </div>
              : null
          }
        </div>
        {
          this.state.errors.length ?
            <div className="editable-text__errors">
              {
                this.state.errors.map(e => (
                  <span key={e} className="editable-text__error">
                    <Localized
                      id={e}
                      vars={{
                        min: this.props.minLength || 0,
                        max: this.props.maxLength || 1000,
                      }}
                    >
                      {e}
                    </Localized>
                  </span>
                ))
              }
            </div>
            : null
        }
      </form>
    )
  }

  private handleNameChange = (e: React.FormEvent<HTMLSpanElement>) => {
    const { minLength, maxLength } = this.props
    const errors: EditableTextError[] = []
    const text = e.currentTarget.innerText
    if (minLength && text.length < minLength) {
      errors.push('editable-text-error-min-length')
    }
    if (maxLength && text.length > maxLength) {
      errors.push('editable-text-error-max-length')
    }
    this.setState({ text, errors })
  }

  private onKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    switch (e.key) {
    case 'Enter':
      e.preventDefault()
      this.onSubmit()
      break

    case 'Escape':
      this.cancelEdit()
      break

    default:
      break
    }
  }

  private onSubmit = () => {
    const { minLength, maxLength } = this.props
    const text = this.state.text
    if (
      text === this.props.text
      || (minLength && text.length < minLength)
      || (maxLength && text.length > maxLength)
    ) {
      return
    }
    this.props.onAccept(this.state.text)
  }

  private cancelEdit = () => {
    this.setState({ text: this.props.text, errors: [] })
    if (this.textRef.current) {
      this.textRef.current.innerText = this.props.text
    }
  }
}

export default withLocalization(EditableText)
